/**
 * Dash lists — bullet lists that render a dash marker.
 *
 * Lexical has one unordered list type, so the dash lives in the list items'
 * `list-style-type` (the only per-node style a list item applies to its DOM)
 * and in the markdown marker character, which is what survives a save. `-`
 * and `*` stay bullets so nothing already written changes meaning.
 */
import { $isListItemNode, $isListNode, ListNode } from "@lexical/list";
import { UNORDERED_LIST, type ElementTransformer } from "@lexical/markdown";
import { $findMatchingParent } from "@lexical/utils";
import type { LexicalEditor } from "lexical";

const DASH_MARKER = "+";

export const DASH_LIST_ITEM_STYLE = 'list-style-type: "–  "';

export function $isDashList(node: ListNode): boolean {
  const first = node.getFirstChild();
  return $isListItemNode(first) && first.getStyle() === DASH_LIST_ITEM_STYLE;
}

export function $setDashList(node: ListNode, dash: boolean): void {
  const style = dash ? DASH_LIST_ITEM_STYLE : "";
  for (const child of node.getChildren()) {
    if ($isListItemNode(child) && child.getStyle() !== style) {
      child.setStyle(style);
    }
  }
}

/** Enter starts a row without the style, so every row follows the first. */
export function registerDashListMarkers(editor: LexicalEditor): () => void {
  return editor.registerNodeTransform(ListNode, (node) => {
    if (node.getListType() !== "bullet") return;
    $setDashList(node, $isDashList(node));
  });
}

export const UNORDERED_LIST_WITH_DASHES: ElementTransformer = {
  ...UNORDERED_LIST,
  export: (node, exportChildren) => {
    const output = UNORDERED_LIST.export(node, exportChildren);
    if (output === null || !$isListNode(node) || !$isDashList(node)) {
      return output;
    }
    return output.replace(/^(\s*)[-*+] /gm, `$1${DASH_MARKER} `);
  },
  replace: (parentNode, children, match, isImport) => {
    UNORDERED_LIST.replace(parentNode, children, match, isImport);
    if (match[0].trim()[0] !== DASH_MARKER || children.length === 0) return;
    const list = $findMatchingParent(children[0], $isListNode);
    if ($isListNode(list)) $setDashList(list, true);
  },
};
